<template>
  <main class="card-pin-page" :class="{ 'is-modifying': isModify }">
    <AppNavBar :title="pageTitle" backgroundColor="#fff" />

    <section class="pin-form">
      <div v-if="isModify" class="pin-field-group">
        <label class="pin-field-label">{{ t("card.currentCardPin") }}</label>
        <van-field
          v-model="currentPin"
          class="pin-input"
          :type="showCurrentPin ? 'text' : 'password'"
          inputmode="numeric"
          maxlength="6"
          :placeholder="t('card.enterCardPin')"
        >
          <template #button>
            <button
              type="button"
              class="pin-eye-button"
              :aria-label="
                showCurrentPin ? t('card.hideCardPin') : t('card.showCardPin')
              "
              @click="showCurrentPin = !showCurrentPin"
            >
              <app-image
                :src="showCurrentPin ? eyeOpenIcon : eyeCloseIcon"
                alt=""
              />
            </button>
          </template>
        </van-field>
      </div>

      <div class="pin-field-group">
        <label class="pin-field-label">
          {{ isModify ? t("card.setNewCardPin") : t("card.cardPin") }}
        </label>
        <van-field
          v-model="pin"
          class="pin-input"
          :type="showPin ? 'text' : 'password'"
          inputmode="numeric"
          maxlength="6"
          :placeholder="
            isModify
              ? t('card.setNewCardPinPlaceholder')
              : t('card.enterCardPin')
          "
        >
          <template #button>
            <button
              type="button"
              class="pin-eye-button"
              :aria-label="
                showPin ? t('card.hideCardPin') : t('card.showCardPin')
              "
              @click="showPin = !showPin"
            >
              <app-image :src="showPin ? eyeOpenIcon : eyeCloseIcon" alt="" />
            </button>
          </template>
        </van-field>
      </div>

      <div class="pin-field-group">
        <label class="pin-field-label">
          {{
            isModify ? t("card.confirmNewCardPin") : t("card.confirmCardPin")
          }}
        </label>
        <van-field
          v-model="confirmPin"
          class="pin-input"
          :type="showConfirmPin ? 'text' : 'password'"
          inputmode="numeric"
          maxlength="6"
          :placeholder="
            isModify
              ? t('card.confirmNewCardPinPlaceholder')
              : t('card.confirmCardPinPlaceholder')
          "
        >
          <template #button>
            <button
              type="button"
              class="pin-eye-button"
              :aria-label="
                showConfirmPin ? t('card.hideCardPin') : t('card.showCardPin')
              "
              @click="showConfirmPin = !showConfirmPin"
            >
              <app-image
                :src="showConfirmPin ? eyeOpenIcon : eyeCloseIcon"
                alt=""
              />
            </button>
          </template>
        </van-field>
      </div>
    </section>

    <van-button
      block
      round
      type="primary"
      class="pin-submit-button"
      :class="{ 'is-modifying': isModify }"
      :disabled="!canSubmit"
      :loading="submitting"
      @click="submitPin"
    >
      {{ isModify ? t("common.confirm") : t("card.confirmSetCardPin") }}
    </van-button>

    <PhysicalSecondCheckPopup
      v-model:show="showPhysicalSecondCheckPopup"
      :confirm-loading="submitting"
      @confirm="confirmPhysicalSecondCheck"
    />
  </main>
</template>

<script setup>
import { computed, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { pcCardUpdatePIN } from "@/api/card";
import { useProfileStore } from "@/stores/profile";
import { getRouteState } from "@/utils/route-state";
import PhysicalSecondCheckPopup from "@/components/PhysicalSecondCheckPopup.vue";
import eyeOpenIcon from "@/assets/images/eye_open_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close_icon.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, "card-pin", {}));
const isModify = computed(() => pageState.value.mode === "modify");
const pageTitle = computed(() => {
  if (pageState.value.mode === "modify") return t("card.modifyCardPin");
  if (pageState.value.mode === "reset") return t("card.resetCardPin");
  return t("card.setCardPinTitle");
});

const currentPin = ref("");
const pin = ref("");
const confirmPin = ref("");
const showCurrentPin = ref(false);
const showPin = ref(false);
const showConfirmPin = ref(false);
const submitting = ref(false);
const showPhysicalSecondCheckPopup = ref(false);

const canSubmit = computed(
  () =>
    (!isModify.value || /^\d{6}$/.test(currentPin.value)) &&
    /^\d{6}$/.test(pin.value) &&
    pin.value === confirmPin.value,
);

async function submitPin() {
  if (!profileStore.platformAddress || !pageState.value.cardId) {
    router.replace("/home/card");
    return;
  }
  if (isModify.value && !/^\d{6}$/.test(currentPin.value)) {
    showFailToast(t("card.sixDigitCardPin"));
    return;
  }
  if (!/^\d{6}$/.test(pin.value)) {
    showFailToast(t("card.sixDigitCardPin"));
    return;
  }
  if (pin.value !== confirmPin.value) {
    showFailToast(t("auth.passwordMismatch"));
    return;
  }

  showPhysicalSecondCheckPopup.value = true;
}

async function confirmPhysicalSecondCheck() {
  if (submitting.value) {
    return;
  }

  showPhysicalSecondCheckPopup.value = false;
  submitting.value = true;
  showLoadingToast({
    message: t("common.submitting"),
    forbidClick: true,
    duration: 0,
  });
  try {
    await pcCardUpdatePIN({
      address: profileStore.platformAddress,
      cardId: pageState.value.cardId,
      pin: pin.value,
      ...(isModify.value ? { oldPin: currentPin.value } : {}),
    });
    closeToast();
    showPassedToast(t("card.cardPinSetSuccess"));
    router.back();
  } catch (_) {
    closeToast();
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.card-pin-page {
  min-height: var(--app-viewport-height);
  padding: 15px 16px 34px;
  background: #fff;
}

.pin-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pin-field-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pin-field-label {
  color: #333;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
}

.pin-input {
  height: 42px;
  padding: 0 12px 0 16px;
  border-radius: 10px;
  background: #f4f5f6;
  align-items: center;

  :deep(.van-field__control) {
    color: #333;
    font-size: 14px;
    line-height: 20px;
  }

  :deep(.van-field__control::placeholder) {
    color: #666;
    opacity: 1;
  }

  :deep(.van-field__button) {
    display: flex;
    align-items: center;
  }
}

.pin-eye-button {
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
}

.pin-submit-button {
  margin-top: 40px;

  &.is-modifying {
    margin-top: 48px;
  }

  &:deep(.van-button__text) {
    font-size: 16px;
    font-weight: 600;
  }

  &:deep(.van-button--disabled) {
    color: #999;
    background: #e0e0e0;
    border-color: #e0e0e0;
    opacity: 1;
  }
}
</style>
